import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemIcon, ListItemText, IconButton, Avatar, Menu, MenuItem,
  Divider, ListItemButton
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Article, Pages, Category,
  LocalHospital, Comment, Image, People, Logout, Settings,
  Event, School, Replay, VpnKey
} from '@mui/icons-material';

import Login from './pages/Login';
import DashboardPage from './pages/Dashboard';
import PagesPage from './pages/Pages';
import PageEditor from './pages/PageEditor';
import ArticlesPage from './pages/Articles';
import ArticleEditor from './pages/ArticleEditor';
import CategoriesPage from './pages/Categories';
import CentresPage from './pages/Centres';
import CentreEditor from './pages/CentreEditor';
import TemoignagesPage from './pages/Temoignages';
import MediaPage from './pages/Media';
import UsersPage from './pages/Users';
import SessionsPage from './pages/Sessions';
import SessionEditor from './pages/SessionEditor';
import ExpertsPage from './pages/Experts';
import ExpertEditor from './pages/ExpertEditor';
import ApiKeysPage from './pages/ApiKeys';

const drawerWidth = 260;

const menuItems = [
  { text: 'Tableau de bord', icon: <Dashboard />, path: '/' },
  { text: 'Sessions', icon: <Event />, path: '/sessions' },
  { text: 'Experts', icon: <School />, path: '/experts' },
  { text: 'Pages', icon: <Pages />, path: '/pages' },
  { text: 'Articles', icon: <Article />, path: '/articles' },
  { text: 'Categories', icon: <Category />, path: '/categories' },
  { text: 'Centres', icon: <LocalHospital />, path: '/centres' },
  { text: 'Temoignages', icon: <Comment />, path: '/temoignages' },
  { text: 'Medias', icon: <Image />, path: '/media' },
  { text: 'Utilisateurs', icon: <People />, path: '/users', adminOnly: true },
  { text: 'API Keys', icon: <VpnKey />, path: '/apikeys', adminOnly: true },
];

function App() {
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  if (!user && location.pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }

  if (location.pathname === '/login') {
    return <Login onLogin={setUser} />;
  }

  const drawer = (
    <Box>
      <Toolbar sx={{ justifyContent: 'center', py: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          Bibliose
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => {
          if (item.adminOnly && !['admin', 'superadmin'].includes(user?.role)) return null;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}
        color="inherit"
        elevation={1}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Administration
          </Typography>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {user?.name?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Deconnexion
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth } }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/sessions" element={<SessionsPage />} />
          <Route path="/sessions/new" element={<SessionEditor />} />
          <Route path="/sessions/:id" element={<SessionEditor />} />
          <Route path="/experts" element={<ExpertsPage />} />
          <Route path="/experts/new" element={<ExpertEditor />} />
          <Route path="/experts/:id" element={<ExpertEditor />} />
          <Route path="/pages" element={<PagesPage />} />
          <Route path="/pages/new" element={<PageEditor />} />
          <Route path="/pages/:id" element={<PageEditor />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/new" element={<ArticleEditor />} />
          <Route path="/articles/:id" element={<ArticleEditor />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/centres" element={<CentresPage />} />
          <Route path="/centres/new" element={<CentreEditor />} />
          <Route path="/centres/:id" element={<CentreEditor />} />
          <Route path="/temoignages" element={<TemoignagesPage />} />
          <Route path="/media" element={<MediaPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/apikeys" element={<ApiKeysPage />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default App;
